// Floating warmup-arena cards over the live Skyforge scene.

import { deserializeGameState, type ServerMessage } from '@shared/protocol';
import type { GameState, Player } from '@shared/game';
import {
  COLOR_ID_TO_HEX,
  DEFAULT_CHARACTER_CUSTOMIZATION,
  type CharacterCustomization,
  type ColorId,
} from '@shared/game/types';
import {
  buildWarmupSubscribeMessage,
  buildPortalReturnUrl,
  setTutorialsHidden,
  hasSeenWarmupTutorial,
  markWarmupTutorialSeen,
  readPreferredCustomization,
  readPreferredPlayerName,
  savePreferredCustomization,
  savePreferredPlayerName,
  type PortalMainMenuPayload,
} from '../../portal-entry';
import type { GsapTimelineHandle } from '../../renderer/vfx/gsap-registry';
import type { CharacterPreviewAdapter } from './character-preview-adapter';
import type { BoardMountPayload } from './board-payload';
import { itemIconUrl } from './item-assets';
import { buildInventoryItemMeta } from './item-ui-meta';
import type { InventoryViewPayload } from './inventory-view';
import {
  createCharacterSelectScreen,
  type CharacterCustomizeCameraRequest,
} from './character-select';
import { openJoinRoomModal, type JoinRoomModalHandle } from './join-room-modal';
import type { PauseMenuPayload } from './pause-menu';
import type { ScreenBackTarget, ScreenHandle, ScreenMountContext } from './types';
import {
  createLiveTutorialWidget,
  type LiveTutorialStep,
  type LiveTutorialWidgetHandle,
} from '../components/live-tutorial-widget';
import { getInputModality } from '../../input/modality';

export interface WarmupTutorialInputBridge {
  setActive(active: boolean): void;
  setAdvanceHandler(handler: (() => void) | null): void;
}

type WarmupMatchmakingPlayer = {
  id: string;
  name: string;
  color?: ColorId;
};

type WarmupMatchmakingSnapshot = {
  players: WarmupMatchmakingPlayer[];
  capacity: number;
  countdownMs: number | null;
  receivedAt: number;
  statusText: string;
};

export type WarmupArenaOverlayPayload = {
  send: (msg: unknown) => void;
  subscribe: (handler: (msg: unknown) => void) => () => void;
  characterAdapter?: CharacterPreviewAdapter | null;
  onCustomizeCamera?: (request: CharacterCustomizeCameraRequest) => void;
  areCharacterCosmeticsLoaded?: () => boolean;
  ensureCharacterCosmeticsLoaded?: () => Promise<void>;
  portal?: PortalMainMenuPayload | null;
  playSfx?: BoardMountPayload['playSfx'];
  previewItemInHand?: BoardMountPayload['previewItemInHand'];
  clearItemInHandPreview?: BoardMountPayload['clearItemInHandPreview'];
  isActionControlLocked?: BoardMountPayload['isActionControlLocked'];
  subscribeActionControlLock?: BoardMountPayload['subscribeActionControlLock'];
  startItemPlayerTargeting?: BoardMountPayload['startItemPlayerTargeting'];
  startItemDirectionalTargeting?: BoardMountPayload['startItemDirectionalTargeting'];
  startItemRangePreview?: BoardMountPayload['startItemRangePreview'];
  getPlayerNotificationAnchor?: BoardMountPayload['getPlayerNotificationAnchor'];
  warmupTutorialInput?: WarmupTutorialInputBridge;
  warmupTutorial?: WarmupTutorialController;
  initialState?: GameState | null;
  initialPlayerId?: string | null;
  initialMatchmaking?: WarmupMatchmakingSnapshot | null;
};

type WarmupCardConfig = {
  key: string;
  label: string;
  icon: string;
  tone: 'pink' | 'green' | 'gold' | 'blue' | 'orange' | 'slate';
};

const CARD_COLORS: Record<
  WarmupCardConfig['tone'],
  { top: string; bottom: string; border: string }
> = {
  pink: { top: '#D85878', bottom: '#9C2E4E', border: '#641D34' },
  green: { top: '#6CC050', bottom: '#488A30', border: '#2E5E1C' },
  gold: { top: '#F0B838', bottom: '#C4841C', border: '#8A5A10' },
  blue: { top: '#5CA0E8', bottom: '#3A6FB0', border: '#264A78' },
  orange: { top: '#F0A040', bottom: '#C07020', border: '#8A5018' },
  slate: { top: '#566178', bottom: '#31394A', border: '#202838' },
};

const FONT_HEADLINE =
  "var(--typography-family-headline, 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif)";
const FONT_BODY =
  "var(--typography-family-body, 'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif)";

const WARMUP_MATCHMAKING_CAPACITY = 4;
const WARMUP_MATCHMAKING_COUNTDOWN_MS = 5 * 60 * 1000;
const WARMUP_MATCHMAKING_TICK_SECONDS = 0.25;
const WARMUP_MATCHMAKING_MAX_SECONDS = 5 * 60 + 2;
const WARMUP_MATCHMAKING_STATUS_TEXT =
  'Waiting for other players to join. Empty slots will be filled with bots.';
const TUTORIAL_Z_INDEX = 5000;

type WarmupTutorialStepId =
  | 'welcome'
  | 'controls'
  | 'arena-effect'
  | 'open-items'
  | 'use-item'
  | 'ready'
  | 'game-modes'
  | 'menu'
  | 'customize'
  | 'complete';

type WarmupTutorialStep = LiveTutorialStep & {
  readonly id: WarmupTutorialStepId;
};

type WarmupTutorialBaseline = {
  nodeId: string | null;
  hp: number | null;
  keys: number | null;
  itemCount: number;
  eventCount: number;
  customization: CharacterCustomization | null;
};

export type WarmupTutorialController = {
  stepIndex: number;
  baseline: WarmupTutorialBaseline | null;
  customizeOpened: boolean;
};

const WARMUP_TUTORIAL_STEPS: readonly WarmupTutorialStep[] = [
  {
    id: 'welcome',
    title: 'GREETINGS',
    prompt: 'Welcome to the warmup arena',
    detail: 'Hey friend! This is where you can try movement and items before joining a match.',
    iconKind: 'd20',
    advanceText: 'START',
    placement: 'top',
  },
  {
    id: 'controls',
    title: 'CONTROLS',
    prompt: 'Choose what feels good',
    detail: 'Tap board tiles to move, then tap visible buttons and targets to choose actions.',
    iconText: 'MOVE',
    advanceText: 'NEXT',
    placement: 'top',
  },
  {
    id: 'arena-effect',
    title: 'MOVE + ITEMS',
    prompt: 'Move around and pick up an item',
    detail: 'Step onto item tiles until gear appears in your bag.',
    iconText: 'MOVE',
    manualAdvance: true,
    waitingText: 'PICK UP',
    placement: 'top',
  },
  {
    id: 'open-items',
    title: 'ITEMS',
    prompt: 'Open your Items panel',
    detail: 'Your practice gear lives here.',
    iconText: 'BAG',
    manualAdvance: true,
    waitingText: 'OPEN',
    placement: 'top',
  },
  {
    id: 'use-item',
    title: 'USE ITEMS',
    prompt: 'Use items on targets',
    detail: 'Open Items, then tap characters, tiles, or direction arrows to choose targets.',
    iconText: 'AIM',
    manualAdvance: true,
    waitingText: 'USE',
    placement: 'bottom',
  },
  {
    id: 'ready',
    title: 'READY UP',
    prompt: 'Ready enters quick matchmaking',
    detail:
      'Ready joins a queue with strangers. To play solo or with friends, use the top buttons.',
    iconText: 'GO',
    placement: 'right',
  },
  {
    id: 'game-modes',
    title: 'GAME MODES',
    prompt: 'Choose how you want to play',
    detail: 'Host, join, play solo minigames, or queue for minigames only.',
    iconText: 'MODE',
    placement: 'bottom',
  },
  {
    id: 'menu',
    title: 'MENU',
    prompt: 'Settings and reminders live here',
    detail:
      'Use the menu for settings and a quick How To Play reminder. Your first real game has its own tutorial.',
    iconText: 'MENU',
    advanceText: 'NEXT',
    placement: 'left',
    placementOffsetY: -24,
  },
  {
    id: 'customize',
    title: 'CUSTOMIZE',
    prompt: 'Change your name and customize your character',
    detail: 'Open Customize and change your look when you want a different character.',
    iconText: 'YOU',
    manualAdvance: true,
    waitingText: 'CHANGE',
    placement: 'right',
    placementOffsetY: -24,
  },
  {
    id: 'complete',
    title: 'COMPLETE',
    prompt: 'Tutorial complete',
    detail:
      "Great, now you're ready to play. Use Ready for quick matchmaking, or choose a mode from the top buttons.",
    iconText: 'GO',
    advanceText: 'DONE',
    placement: 'top',
    placementOffsetY: 220,
  },
] as const;

type WarmupMatchmakingStatusMessage = Extract<ServerMessage, { type: 'warmup_matchmaking_status' }>;

function formatWarmupCountdown(ms: number): string {
  const totalSeconds = Math.ceil(Math.max(0, ms) / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function isColorId(value: unknown): value is ColorId {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(COLOR_ID_TO_HEX, value);
}

function colorIdToCss(color: ColorId): string {
  return `#${COLOR_ID_TO_HEX[color].toString(16).padStart(6, '0').toUpperCase()}`;
}

function cloneCustomization(
  customization: CharacterCustomization | null,
): CharacterCustomization | null {
  return customization == null ? null : { ...customization };
}

function hasCustomizationChanged(
  before: CharacterCustomization | null,
  after: CharacterCustomization | null,
): boolean {
  if (before == null || after == null) return false;
  return (
    before.color !== after.color ||
    before.head !== after.head ||
    before.eyes !== after.eyes ||
    before.cape !== after.cape
  );
}

function isTypingTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    target.closest('input, textarea, select, [contenteditable="true"]') != null
  );
}

function isTouchUi(): boolean {
  return getInputModality() === 'touch';
}

function isPhoneWarmupLayout(): boolean {
  const viewport = window.visualViewport;
  const width = viewport?.width ?? window.innerWidth;
  return width < 700 || (isTouchUi() && width < 900);
}

function readWarmupTutorialStepOverride(): number | null {
  try {
    const raw = new URLSearchParams(window.location.search).get('e2eWarmupTutorialStep');
    if (raw == null) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function makeCard(config: WarmupCardConfig): HTMLButtonElement {
  const color = CARD_COLORS[config.tone];
  const button = document.createElement('button');
  button.type = 'button';
  button.dataset.arenaCard = config.key;
  button.style.cssText = [
    'height: 62px',
    'min-width: 148px',
    'display: grid',
    'grid-template-columns: 28px minmax(0, 1fr)',
    'align-items: center',
    'gap: 8px',
    'padding: 9px 12px',
    `background: linear-gradient(to bottom, ${color.top}, ${color.bottom})`,
    `border: 2px solid ${color.border}`,
    'border-radius: 8px',
    'box-shadow: 0 4px 0 rgba(0,0,0,0.45)',
    'box-sizing: border-box',
    'cursor: pointer',
    'color: #FFFFFF',
    'text-align: left',
    'pointer-events: auto',
  ].join('; ');

  const icon = document.createElement('span');
  icon.dataset.arenaCardIcon = 'true';
  icon.textContent = config.icon;
  icon.setAttribute('aria-hidden', 'true');
  icon.style.cssText = [
    'width: 28px',
    'height: 28px',
    'display: grid',
    'place-items: center',
    'border-radius: 999px',
    'background: rgba(0,0,0,0.16)',
    'font-size: 16px',
    'line-height: 1',
  ].join('; ');
  button.appendChild(icon);

  const label = document.createElement('span');
  label.dataset.arenaCardLabel = 'true';
  label.textContent = config.label;
  label.style.cssText = [
    `font-family: ${FONT_HEADLINE}`,
    'font-size: 15px',
    'font-weight: 800',
    'letter-spacing: 0',
    'line-height: 1.05',
    'text-transform: uppercase',
    'text-shadow: 0 1px 0 #000000, 0 2px 6px rgba(0,0,0,0.45)',
    'overflow-wrap: anywhere',
  ].join('; ');
  button.appendChild(label);
  return button;
}

function makePanelButton(
  label: string,
  tone: 'primary' | 'secondary' | 'ghost',
): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = label;
  const primary = tone === 'primary';
  const ghost = tone === 'ghost';
  button.style.cssText = [
    'min-height: 44px',
    'min-width: 82px',
    'padding: 0 12px',
    'border-radius: 8px',
    primary
      ? 'background: linear-gradient(to bottom, #F0B838, #C4841C)'
      : ghost
        ? 'background: rgba(255,255,255,0.08)'
        : 'background: rgba(92,160,232,0.24)',
    primary
      ? 'border: 1px solid #8A5A10'
      : ghost
        ? 'border: 1px solid rgba(255,255,255,0.16)'
        : 'border: 1px solid rgba(92,160,232,0.44)',
    'color: #FFFFFF',
    `font-family: ${FONT_HEADLINE}`,
    'font-size: 12px',
    'font-weight: 800',
    'letter-spacing: 0',
    'line-height: 1',
    'text-transform: uppercase',
    primary ? 'text-shadow: 0 1px 0 #000000, 0 2px 6px rgba(0,0,0,0.45)' : '',
    'cursor: pointer',
    'box-sizing: border-box',
    'pointer-events: auto',
    'touch-action: manipulation',
  ].join('; ');
  return button;
}

function getLobbyPayloadBase(payload: WarmupArenaOverlayPayload): {
  playerName: string;
  send: (msg: unknown) => void;
  subscribe: (handler: (msg: unknown) => void) => () => void;
  characterAdapter?: CharacterPreviewAdapter | null;
  preferredCustomization?: ReturnType<typeof readPreferredCustomization>;
} {
  const preferredCustomization = readPreferredCustomization();
  return {
    playerName: readPreferredPlayerName(),
    send: payload.send,
    subscribe: payload.subscribe,
    characterAdapter: payload.characterAdapter,
    ...(preferredCustomization == null ? {} : { preferredCustomization }),
  };
}

export function createWarmupArenaOverlayScreen(
  ctx: ScreenMountContext<WarmupArenaOverlayPayload>,
): ScreenHandle {
  const { host, router } = ctx;
  const payload = ctx.payload;
  let disposed = false;
  let localPlayerId: string | null = payload.initialPlayerId ?? null;
  let latestState: GameState | null = payload.initialState ?? null;
  let preferredPlayerName = readPreferredPlayerName();
  let warmupReady = false;
  let pendingWarmupReady: boolean | null = null;
  let customizationCosmeticsLoading = false;
  let customizationOverlayHandle: ScreenHandle | null = null;
  let customizationOverlayRoot: HTMLElement | null = null;
  let joinModalHandle: JoinRoomModalHandle | null = null;
  let tutorialHandle: LiveTutorialWidgetHandle | null = null;
  let warmupHudSuppressed = false;
  const warmupTutorial: WarmupTutorialController = payload.warmupTutorial ?? {
    stepIndex: readWarmupTutorialStepOverride() ?? 0,
    baseline: null,
    customizeOpened: false,
  };
  warmupTutorial.customizeOpened = warmupTutorial.customizeOpened === true;
  let watchBackButton: HTMLButtonElement | null = null;
  let matchmakingPlayers: WarmupMatchmakingPlayer[] = payload.initialMatchmaking?.players ?? [];
  let matchmakingCapacity = payload.initialMatchmaking?.capacity ?? WARMUP_MATCHMAKING_CAPACITY;
  let matchmakingCountdownMs = payload.initialMatchmaking?.countdownMs ?? null;
  let matchmakingReceivedAt = payload.initialMatchmaking?.receivedAt ?? 0;
  let matchmakingStatusText =
    payload.initialMatchmaking?.statusText ?? WARMUP_MATCHMAKING_STATUS_TEXT;
  let matchmakingTicker: GsapTimelineHandle | null = null;
  const now = () => performance.now();

  const warmupBackTarget: ScreenBackTarget = {
    screenId: 'warmup-arena-overlay',
    payload,
    beforeNavigate: () => {
      payload.send({ type: 'leave' });
      payload.send(
        buildWarmupSubscribeMessage({
          preferredColor: payload.portal?.colorHex,
          isReady: hasSeenWarmupTutorial(),
        }),
      );
    },
  };

  const root = document.createElement('div');
  root.className = 'screen-warmup-arena-overlay';
  root.setAttribute('data-screen', 'warmup-arena-overlay');
  root.style.cssText = [
    'position: fixed',
    'inset: 0',
    'z-index: 120',
    'pointer-events: none',
    'display: flex',
    'align-items: flex-start',
    'justify-content: center',
    'padding: 14px',
    'box-sizing: border-box',
  ].join('; ');

  const menuTutorialTarget = document.createElement('div');
  menuTutorialTarget.dataset.testid = 'arena-menu-tutorial-target';
  menuTutorialTarget.style.cssText = [
    'position: fixed',
    'top: max(18px, env(safe-area-inset-top))',
    'right: max(18px, env(safe-area-inset-right))',
    'width: 48px',
    'height: 48px',
    'opacity: 0',
    'pointer-events: none',
  ].join('; ');
  root.appendChild(menuTutorialTarget);

  const tray = document.createElement('div');
  tray.style.cssText = [
    'display: flex',
    'flex-direction: column',
    'justify-content: center',
    'align-items: center',
    'gap: 10px',
    'max-width: min(840px, calc(100vw - 28px))',
    'pointer-events: none',
  ].join('; ');
  root.appendChild(tray);

  function makeActionRow(): HTMLDivElement {
    const row = document.createElement('div');
    row.style.cssText = [
      'display: flex',
      'flex-wrap: wrap',
      'justify-content: center',
      'align-items: center',
      'gap: 10px',
      'max-width: min(832px, calc(100vw - 28px))',
      'pointer-events: none',
    ].join('; ');
    return row;
  }

  const topActionRow = makeActionRow();
  const bottomActionRow = makeActionRow();
  const watchBackRow = makeActionRow();
  watchBackRow.dataset.testid = 'arena-watch-back-row';
  watchBackRow.style.display = 'none';
  tray.append(topActionRow, bottomActionRow, watchBackRow);

  const cards: WarmupCardConfig[] = [
    { key: 'host-game', label: 'Host Game', icon: '⌂', tone: 'gold' },
    { key: 'join-game', label: 'Join Game', icon: '🔗', tone: 'green' },
    { key: 'solo-minigame', label: 'Solo Minigame', icon: '🎮', tone: 'pink' },
    { key: 'minigames-only', label: 'Minigames Only', icon: '🎲', tone: 'orange' },
    { key: 'watch-active-game', label: 'Look At Game', icon: '↗', tone: 'blue' },
    { key: 'how-to-play', label: 'How To Play', icon: '?', tone: 'slate' },
  ];
  if (payload.portal != null) {
    cards.push({ key: 'return-portal', label: 'Return Portal', icon: '↗', tone: 'blue' });
  }

  const cardEls = new Map<string, HTMLButtonElement>();
  const primaryActionKeys = new Set([
    'host-game',
    'join-game',
    'solo-minigame',
    'minigames-only',
    'watch-active-game',
    'how-to-play',
  ]);
  const cardOrder = cards.map((card) => card.key);
  cards.forEach((card, index) => {
    const el = makeCard(card);
    (index < 4 ? topActionRow : bottomActionRow).appendChild(el);
    cardEls.set(card.key, el);
  });

  function arrangeActionCardsForLayout(phone: boolean): void {
    if (phone) {
      for (const key of cardOrder) {
        const el = cardEls.get(key);
        if (el == null) continue;
        (primaryActionKeys.has(key) ? topActionRow : bottomActionRow).appendChild(el);
      }
      return;
    }

    cardOrder.forEach((key, index) => {
      const el = cardEls.get(key);
      if (el == null) return;
      (index < 4 ? topActionRow : bottomActionRow).appendChild(el);
    });
  }

  const matchmakingHud = document.createElement('section');
  matchmakingHud.dataset.testid = 'arena-matchmaking-hud';
  matchmakingHud.setAttribute('aria-label', 'Warmup matchmaking');
  matchmakingHud.style.cssText = [
    'position: absolute',
    'top: max(14px, env(safe-area-inset-top))',
    'left: max(14px, env(safe-area-inset-left))',
    'width: min(318px, calc(100vw - 28px))',
    'box-sizing: border-box',
    'display: flex',
    'flex-direction: column',
    'gap: 8px',
    'padding: 10px',
    'border-radius: 8px',
    'background: linear-gradient(180deg, rgba(26,32,45,0.84), rgba(15,19,28,0.70))',
    'border: 1px solid rgba(255,255,255,0.16)',
    'box-shadow: 0 12px 28px rgba(0,0,0,0.30)',
    'backdrop-filter: blur(8px)',
    'color: #FFFFFF',
    'pointer-events: none',
  ].join('; ');
  root.appendChild(matchmakingHud);

  const matchmakingHeader = document.createElement('div');
  matchmakingHeader.style.cssText = [
    'display: flex',
    'align-items: flex-start',
    'justify-content: space-between',
    'gap: 10px',
  ].join('; ');
  matchmakingHud.appendChild(matchmakingHeader);

  const matchmakingTitleStack = document.createElement('div');
  matchmakingTitleStack.style.cssText = [
    'display: flex',
    'flex-direction: column',
    'gap: 2px',
  ].join('; ');
  matchmakingHeader.appendChild(matchmakingTitleStack);

  const matchmakingEyebrow = document.createElement('div');
  matchmakingEyebrow.textContent = 'WARMUP';
  matchmakingEyebrow.style.cssText = [
    `font-family: ${FONT_HEADLINE}`,
    'font-size: 11px',
    'font-weight: 800',
    'letter-spacing: 0',
    'color: rgba(232,160,32,0.95)',
    'text-transform: uppercase',
    'text-shadow: 0 1px 2px rgba(0,0,0,0.45)',
  ].join('; ');
  matchmakingTitleStack.appendChild(matchmakingEyebrow);

  const matchmakingTitle = document.createElement('h2');
  matchmakingTitle.textContent = 'MATCHMAKING';
  matchmakingTitle.style.cssText = [
    `font-family: ${FONT_HEADLINE}`,
    'margin: 0',
    'color: #FFFFFF',
    'font-size: 20px',
    'font-weight: 800',
    'line-height: 1',
    'letter-spacing: 0',
    'text-shadow: 0 2px 3px rgba(0,0,0,0.45)',
  ].join('; ');
  matchmakingTitleStack.appendChild(matchmakingTitle);

  const matchmakingCount = document.createElement('div');
  matchmakingCount.dataset.testid = 'arena-matchmaking-count';
  matchmakingCount.style.cssText = [
    `font-family: ${FONT_HEADLINE}`,
    'font-size: 14px',
    'font-weight: 800',
    'line-height: 1',
    'padding: 6px 8px',
    'border-radius: 7px',
    'background: rgba(0,0,0,0.28)',
    'border: 1px solid rgba(255,255,255,0.12)',
    'white-space: nowrap',
  ].join('; ');
  matchmakingHeader.appendChild(matchmakingCount);

  const matchmakingCountdown = document.createElement('div');
  matchmakingCountdown.dataset.testid = 'arena-matchmaking-countdown';
  matchmakingCountdown.style.cssText = [
    `font-family: ${FONT_HEADLINE}`,
    'color: #FFFFFF',
    'font-size: 26px',
    'font-weight: 800',
    'line-height: 1',
    'letter-spacing: 0',
    'padding: 8px 10px',
    'border-radius: 8px',
    'background: rgba(232,160,32,0.14)',
    'border: 1px solid rgba(232,160,32,0.28)',
    'text-shadow: 0 2px 3px rgba(0,0,0,0.45)',
  ].join('; ');
  matchmakingHud.appendChild(matchmakingCountdown);

  const matchmakingStatus = document.createElement('div');
  matchmakingStatus.dataset.testid = 'arena-matchmaking-status';
  matchmakingStatus.style.cssText = [
    `font-family: ${FONT_BODY}`,
    'font-size: 13px',
    'font-weight: 900',
    'line-height: 1.15',
    'color: #D8DFEA',
  ].join('; ');
  matchmakingHud.appendChild(matchmakingStatus);

  const matchmakingPills = document.createElement('div');
  matchmakingPills.style.cssText = [
    'display: flex',
    'flex-wrap: wrap',
    'gap: 6px',
    'opacity: 0.88',
  ].join('; ');
  matchmakingHud.appendChild(matchmakingPills);

  for (const text of ['5 MIN TIMER', 'BOT FILL', '4 PLAYERS START']) {
    const pill = document.createElement('div');
    pill.textContent = text;
    pill.style.cssText = [
      `font-family: ${FONT_HEADLINE}`,
      'font-size: 10px',
      'font-weight: 800',
      'line-height: 1',
      'padding: 5px 7px',
      'border-radius: 999px',
      'background: rgba(0,0,0,0.24)',
      'border: 1px solid rgba(255,255,255,0.10)',
      'white-space: nowrap',
    ].join('; ');
    matchmakingPills.appendChild(pill);
  }

  const matchmakingSlots = document.createElement('div');
  matchmakingSlots.dataset.testid = 'arena-matchmaking-slots';
  matchmakingSlots.style.cssText = ['display: flex', 'flex-wrap: wrap', 'gap: 6px'].join('; ');
  matchmakingHud.appendChild(matchmakingSlots);

  const playerHud = document.createElement('section');
  playerHud.dataset.testid = 'arena-player-panel';
  playerHud.setAttribute('aria-label', 'Warmup player');
  playerHud.style.cssText = [
    'position: absolute',
    'top: max(254px, calc(env(safe-area-inset-top) + 254px))',
    'left: max(14px, env(safe-area-inset-left))',
    'width: min(318px, calc(100vw - 28px))',
    'box-sizing: border-box',
    'display: flex',
    'flex-direction: column',
    'gap: 9px',
    'padding: 10px',
    'border-radius: 8px',
    'background: linear-gradient(180deg, rgba(26,32,45,0.84), rgba(15,19,28,0.70))',
    'border: 1px solid rgba(255,255,255,0.16)',
    'box-shadow: 0 12px 28px rgba(0,0,0,0.30)',
    'backdrop-filter: blur(8px)',
    'color: #FFFFFF',
    'pointer-events: auto',
  ].join('; ');
  root.appendChild(playerHud);

  const nicknameLabel = document.createElement('div');
  nicknameLabel.textContent = 'PLAYER';
  nicknameLabel.style.cssText = [
    `font-family: ${FONT_HEADLINE}`,
    'font-size: 11px',
    'font-weight: 800',
    'line-height: 1',
    'color: rgba(232,160,32,0.95)',
    'text-transform: uppercase',
  ].join('; ');
  playerHud.appendChild(nicknameLabel);

  const nicknameRow = document.createElement('div');
  nicknameRow.style.cssText = [
    'display: grid',
    'grid-template-columns: minmax(0, 1fr) auto',
    'align-items: center',
    'gap: 8px',
  ].join('; ');
  playerHud.appendChild(nicknameRow);

  const nicknameDisplay = document.createElement('div');
  nicknameDisplay.dataset.testid = 'arena-nickname-display';
  nicknameDisplay.style.cssText = [
    `font-family: ${FONT_HEADLINE}`,
    'font-size: 20px',
    'font-weight: 800',
    'line-height: 1',
    'color: #FFFFFF',
    'text-shadow: 0 2px 3px rgba(0,0,0,0.45)',
    'overflow: hidden',
    'text-overflow: ellipsis',
    'white-space: nowrap',
  ].join('; ');
  nicknameRow.appendChild(nicknameDisplay);

  const nicknameEdit = makePanelButton('Edit', 'ghost');
  nicknameEdit.dataset.testid = 'arena-nickname-edit';
  nicknameRow.appendChild(nicknameEdit);

  const nicknameEditRow = document.createElement('div');
  nicknameEditRow.style.cssText = [
    'display: none',
    'grid-template-columns: minmax(0, 1fr) auto auto',
    'align-items: center',
    'gap: 6px',
  ].join('; ');
  playerHud.appendChild(nicknameEditRow);

  const nicknameInput = document.createElement('input');
  nicknameInput.type = 'text';
  nicknameInput.maxLength = 16;
  nicknameInput.spellcheck = false;
  nicknameInput.autocomplete = 'name';
  nicknameInput.style.cssText = [
    'height: 44px',
    'min-width: 0',
    'box-sizing: border-box',
    'border-radius: 8px',
    'border: 1px solid rgba(255,255,255,0.24)',
    'background: rgba(0,0,0,0.30)',
    'color: #FFFFFF',
    `font-family: ${FONT_HEADLINE}`,
    'font-size: 15px',
    'font-weight: 800',
    'letter-spacing: 0',
    'padding: 0 9px',
    'outline: none',
  ].join('; ');
  nicknameEditRow.appendChild(nicknameInput);

  const nicknameSave = makePanelButton('Save', 'primary');
  const nicknameCancel = makePanelButton('Cancel', 'ghost');
  nicknameEditRow.append(nicknameSave, nicknameCancel);

  const nicknameStatus = document.createElement('div');
  nicknameStatus.dataset.testid = 'arena-nickname-status';
  nicknameStatus.style.cssText = [
    `font-family: ${FONT_BODY}`,
    'font-size: 12px',
    'font-weight: 900',
    'line-height: 1.1',
    'color: #B0B8C8',
    'min-height: 14px',
  ].join('; ');
  playerHud.appendChild(nicknameStatus);

  const playerActionRow = document.createElement('div');
  playerActionRow.style.cssText = [
    'display: grid',
    'grid-template-columns: 1fr 1fr',
    'gap: 8px',
  ].join('; ');
  playerHud.appendChild(playerActionRow);

  const readyToggle = makePanelButton('Unready', 'primary');
  readyToggle.dataset.testid = 'arena-ready-toggle';
  const customizeButton = makePanelButton('Customize', 'secondary');
  customizeButton.dataset.testid = 'arena-customize';
  playerActionRow.append(readyToggle, customizeButton);

  const joinPanel = document.createElement('form');
  joinPanel.dataset.testid = 'arena-join-panel';
  joinPanel.style.cssText = [
    'position: absolute',
    'top: 96px',
    'left: 50%',
    'transform: translateX(-50%)',
    'display: none',
    'align-items: center',
    'gap: 8px',
    'max-width: min(390px, calc(100vw - 28px))',
    'padding: 10px',
    'border-radius: 8px',
    'background: rgba(20,24,34,0.88)',
    'border: 1px solid rgba(255,255,255,0.18)',
    'box-shadow: 0 6px 18px rgba(0,0,0,0.30)',
    'pointer-events: auto',
  ].join('; ');

  const joinInput = document.createElement('input');
  joinInput.type = 'text';
  joinInput.placeholder = 'ROOM CODE';
  joinInput.maxLength = 8;
  joinInput.autocapitalize = 'characters';
  joinInput.spellcheck = false;
  joinInput.style.cssText = [
    'width: 126px',
    'height: 44px',
    'box-sizing: border-box',
    'border-radius: 8px',
    'border: 1px solid rgba(255,255,255,0.24)',
    'background: rgba(0,0,0,0.30)',
    'color: #FFFFFF',
    `font-family: ${FONT_HEADLINE}`,
    'font-size: 16px',
    'font-weight: 800',
    'letter-spacing: 0',
    'text-align: center',
    'text-transform: uppercase',
    'outline: none',
  ].join('; ');

  function makeJoinPanelButton(label: string, tone: 'primary' | 'ghost'): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = label === 'JOIN' ? 'submit' : 'button';
    button.textContent = label;
    button.style.cssText = [
      'height: 44px',
      'min-width: 70px',
      'padding: 0 10px',
      'border-radius: 8px',
      tone === 'primary' ? 'background: #E8A020' : 'background: rgba(255,255,255,0.10)',
      tone === 'primary' ? 'border: 1px solid #8A5A10' : 'border: 1px solid rgba(255,255,255,0.18)',
      'color: #FFFFFF',
      `font-family: ${FONT_HEADLINE}`,
      'font-size: 13px',
      'font-weight: 800',
      'letter-spacing: 0',
      tone === 'primary' ? 'text-shadow: 0 1px 0 #000000, 0 2px 6px rgba(0,0,0,0.45)' : '',
      'cursor: pointer',
      'touch-action: manipulation',
    ].join('; ');
    return button;
  }

  const joinSubmit = makeJoinPanelButton('JOIN', 'primary');
  const joinCancel = makeJoinPanelButton('CANCEL', 'ghost');
  joinPanel.append(joinInput, joinSubmit, joinCancel);
  root.appendChild(joinPanel);

  const inventoryPanel = document.createElement('section');
  inventoryPanel.dataset.testid = 'arena-inventory-panel';
  inventoryPanel.setAttribute('aria-label', 'Arena items');
  inventoryPanel.style.cssText = [
    'position: absolute',
    'right: max(14px, env(safe-area-inset-right))',
    'bottom: 86px',
    'width: min(280px, calc(100vw - 28px))',
    'display: flex',
    'align-items: center',
    'gap: 10px',
    'padding: 10px',
    'box-sizing: border-box',
    'border-radius: 8px',
    'background: rgba(20,24,34,0.84)',
    'border: 1px solid rgba(255,255,255,0.18)',
    'box-shadow: 0 6px 18px rgba(0,0,0,0.30)',
    'pointer-events: auto',
    'cursor: pointer',
  ].join('; ');
  inventoryPanel.setAttribute('role', 'button');
  inventoryPanel.tabIndex = 0;
  root.appendChild(inventoryPanel);

  const inventoryPreview = document.createElement('div');
  inventoryPreview.dataset.testid = 'arena-inventory-preview';
  inventoryPreview.style.cssText = [
    'width: 54px',
    'height: 54px',
    'border-radius: 8px',
    'display: grid',
    'place-items: center',
    'overflow: hidden',
    'background: rgba(0,0,0,0.30)',
    'border: 1px solid rgba(232,160,32,0.30)',
    'color: #E8A020',
    `font-family: ${FONT_HEADLINE}`,
    'font-size: 18px',
    'font-weight: 800',
  ].join('; ');
  inventoryPanel.appendChild(inventoryPreview);

  const inventoryText = document.createElement('div');
  inventoryText.style.cssText = [
    'display: flex',
    'flex-direction: column',
    'gap: 4px',
    'min-width: 0',
    'flex: 1',
  ].join('; ');
  inventoryPanel.appendChild(inventoryText);

  const inventoryCount = document.createElement('div');
  inventoryCount.dataset.testid = 'arena-inventory-count';
  inventoryCount.style.cssText = [
    `font-family: ${FONT_HEADLINE}`,
    'font-size: 14px',
    'font-weight: 800',
    'color: #FFFFFF',
    'text-transform: uppercase',
    'text-shadow: 0 1px 0 #000000',
  ].join('; ');
  inventoryText.appendChild(inventoryCount);

  const inventoryHint = document.createElement('div');
  inventoryHint.style.cssText = [
    `font-family: ${FONT_BODY}`,
    'font-size: 12px',
    'font-weight: 900',
    'color: #B0B8C8',
    'line-height: 1.1',
  ].join('; ');
  inventoryText.appendChild(inventoryHint);

  function applyInventoryPanelLayout(): void {
    const narrow = window.innerWidth < 700;
    inventoryPanel.style.bottom = narrow ? '218px' : '86px';
  }

  const mobileDockWidthCss =
    'calc((var(--app-viewport-width, 100vw) - max(10px, env(safe-area-inset-left)) - max(10px, env(safe-area-inset-right)) - 8px) / 2)';
  const mobileRightDockLeftCss = `calc(max(10px, env(safe-area-inset-left)) + (${mobileDockWidthCss}) + 8px)`;
  const mobileBottomDockOffsetCss = 'max(10px, env(safe-area-inset-bottom))';
  const mobileTutorialDockOffsetCss = 'max(116px, calc(env(safe-area-inset-bottom) + 116px))';
  const mobileInventoryDockOffsetCss = 'max(184px, calc(env(safe-area-inset-bottom) + 184px))';
  const mobileTutorialInventoryDockOffsetCss =
    'max(290px, calc(env(safe-area-inset-bottom) + 290px))';

  function applyWarmupResponsiveLayout(): void {
    const phone = isPhoneWarmupLayout();
    const tutorialFocus = isMobileTutorialFocusActive();
    applyInventoryPanelLayout();
    arrangeActionCardsForLayout(phone);

    if (phone) {
      root.style.padding = '0';
      tray.style.position = 'absolute';
      tray.style.top = tutorialFocus
        ? 'max(126px, calc(env(safe-area-inset-top) + 126px))'
        : 'max(68px, calc(env(safe-area-inset-top) + 68px))';
      tray.style.left = 'max(10px, env(safe-area-inset-left))';
      tray.style.right = 'max(10px, env(safe-area-inset-right))';
      tray.style.maxWidth = 'none';
      tray.style.gap = '7px';
      topActionRow.style.display = 'grid';
      topActionRow.style.gridTemplateColumns = 'repeat(3, minmax(0, 1fr))';
      topActionRow.style.width = '100%';
      topActionRow.style.maxWidth = 'none';
      topActionRow.style.gap = '7px';
      bottomActionRow.style.display = bottomActionRow.childElementCount > 0 ? 'flex' : 'none';
      bottomActionRow.style.gridTemplateColumns = '';
      bottomActionRow.style.width = '100%';
      bottomActionRow.style.maxWidth = 'none';
      bottomActionRow.style.justifyContent = 'flex-start';
      bottomActionRow.style.gap = '7px';
      watchBackRow.style.display = watchBackButton == null ? 'none' : 'grid';
      watchBackRow.style.gridTemplateColumns = 'repeat(3, minmax(0, 1fr))';
      watchBackRow.style.width = '100%';
      watchBackRow.style.maxWidth = 'none';
      watchBackRow.style.gap = '7px';
      for (const [key, card] of cardEls) {
        const compactSecondary = !primaryActionKeys.has(key);
        card.style.width = compactSecondary ? 'min(170px, 48vw)' : '100%';
        card.style.minWidth = '0';
        card.style.height = compactSecondary ? '53px' : '55px';
        card.style.minHeight = '44px';
        card.style.gridTemplateColumns = compactSecondary
          ? '26px minmax(0, 1fr)'
          : '26px minmax(0, 1fr)';
        card.style.gap = '7px';
        card.style.padding = compactSecondary ? '7px 10px' : '7px 8px';
        card
          .querySelector<HTMLElement>('[data-arena-card-icon="true"]')
          ?.style.setProperty('width', '26px');
        card
          .querySelector<HTMLElement>('[data-arena-card-icon="true"]')
          ?.style.setProperty('height', '26px');
        card
          .querySelector<HTMLElement>('[data-arena-card-icon="true"]')
          ?.style.setProperty('font-size', '16px');
        card
          .querySelector<HTMLElement>('[data-arena-card-label="true"]')
          ?.style.setProperty('font-size', compactSecondary ? '13px' : '12.5px');
        card
          .querySelector<HTMLElement>('[data-arena-card-label="true"]')
          ?.style.setProperty('line-height', '1');
      }

      matchmakingHud.style.top = 'auto';
      matchmakingHud.style.left = 'max(10px, env(safe-area-inset-left))';
      matchmakingHud.style.right = '';
      matchmakingHud.style.bottom = tutorialFocus
        ? mobileTutorialDockOffsetCss
        : mobileBottomDockOffsetCss;
      matchmakingHud.style.width = mobileDockWidthCss;
      matchmakingHud.style.maxHeight = '164px';
      matchmakingHud.style.overflow = 'hidden';
      matchmakingHud.style.padding = '8px';
      matchmakingHud.style.gap = '6px';
      matchmakingHud.style.zIndex = '430';
      matchmakingHeader.style.gap = '6px';
      matchmakingTitleStack.style.gap = '1px';
      matchmakingEyebrow.style.fontSize = '11px';
      matchmakingTitle.style.fontSize = '15px';
      matchmakingCount.style.fontSize = '13px';
      matchmakingCount.style.padding = '6px 7px';
      matchmakingCountdown.style.fontSize = '18px';
      matchmakingCountdown.style.padding = '6px 7px';
      matchmakingStatus.style.fontSize = '13px';
      matchmakingStatus.style.lineHeight = '1.08';
      matchmakingPills.style.display = 'none';
      matchmakingSlots.style.gap = '5px';
      playerHud.style.top = 'auto';
      playerHud.style.left = mobileRightDockLeftCss;
      playerHud.style.right = 'max(10px, env(safe-area-inset-right))';
      playerHud.style.bottom = tutorialFocus
        ? mobileTutorialDockOffsetCss
        : mobileBottomDockOffsetCss;
      playerHud.style.width = 'auto';
      playerHud.style.height = tutorialFocus ? '' : '164px';
      playerHud.style.padding = '8px';
      playerHud.style.gap = '6px';
      playerHud.style.zIndex = '440';
      nicknameLabel.style.fontSize = '11px';
      nicknameRow.style.gap = '6px';
      nicknameDisplay.style.fontSize = '18px';
      nicknameEdit.style.minWidth = '70px';
      nicknameEdit.style.padding = '0 9px';
      nicknameEditRow.style.gridTemplateColumns = '1fr 1fr';
      nicknameInput.style.gridColumn = '1 / -1';
      nicknameStatus.style.fontSize = '12px';
      nicknameStatus.style.minHeight = '12px';
      playerActionRow.style.gap = '7px';
      readyToggle.style.minWidth = '0';
      readyToggle.style.padding = '0 7px';
      customizeButton.style.minWidth = '0';
      customizeButton.style.padding = '0 7px';

      inventoryPanel.style.left = mobileRightDockLeftCss;
      inventoryPanel.style.right = 'max(10px, env(safe-area-inset-right))';
      inventoryPanel.style.bottom = tutorialFocus
        ? mobileTutorialInventoryDockOffsetCss
        : mobileInventoryDockOffsetCss;
      inventoryPanel.style.width = 'auto';
      inventoryPanel.style.padding = '8px';
      inventoryPanel.style.gap = '8px';
      inventoryPreview.style.width = '46px';
      inventoryPreview.style.height = '46px';
      inventoryCount.style.fontSize = '13px';
      inventoryHint.style.fontSize = '11px';
      return;
    }

    root.style.padding = '14px';
    arrangeActionCardsForLayout(false);
    tray.style.position = '';
    tray.style.top = '';
    tray.style.left = '';
    tray.style.right = '';
    tray.style.maxWidth = 'min(840px, calc(100vw - 28px))';
    tray.style.gap = '10px';
    for (const row of [topActionRow, bottomActionRow]) {
      row.style.display = 'flex';
      row.style.gridTemplateColumns = '';
      row.style.width = '';
      row.style.maxWidth = 'min(832px, calc(100vw - 28px))';
      row.style.gap = '10px';
    }
    watchBackRow.style.display = watchBackButton == null ? 'none' : 'flex';
    watchBackRow.style.gridTemplateColumns = '';
    watchBackRow.style.width = '';
    watchBackRow.style.maxWidth = 'min(832px, calc(100vw - 28px))';
    watchBackRow.style.gap = '10px';
    for (const card of cardEls.values()) {
      card.style.width = '';
      card.style.minWidth = '148px';
      card.style.height = '62px';
      card.style.minHeight = '';
      card.style.gridTemplateColumns = '28px minmax(0, 1fr)';
      card.style.gap = '8px';
      card.style.padding = '9px 12px';
      card
        .querySelector<HTMLElement>('[data-arena-card-icon="true"]')
        ?.style.setProperty('width', '28px');
      card
        .querySelector<HTMLElement>('[data-arena-card-icon="true"]')
        ?.style.setProperty('height', '28px');
      card
        .querySelector<HTMLElement>('[data-arena-card-icon="true"]')
        ?.style.setProperty('font-size', '16px');
      card
        .querySelector<HTMLElement>('[data-arena-card-label="true"]')
        ?.style.setProperty('font-size', '15px');
      card
        .querySelector<HTMLElement>('[data-arena-card-label="true"]')
        ?.style.setProperty('line-height', '1.05');
    }

    matchmakingHud.style.top = 'max(14px, env(safe-area-inset-top))';
    matchmakingHud.style.left = 'max(14px, env(safe-area-inset-left))';
    matchmakingHud.style.right = '';
    matchmakingHud.style.bottom = '';
    matchmakingHud.style.width = 'min(318px, calc(100vw - 28px))';
    matchmakingHud.style.maxHeight = '';
    matchmakingHud.style.overflow = '';
    matchmakingHud.style.padding = '10px';
    matchmakingHud.style.gap = '8px';
    matchmakingHud.style.zIndex = '';
    matchmakingHeader.style.gap = '10px';
    matchmakingTitleStack.style.gap = '2px';
    matchmakingEyebrow.style.fontSize = '11px';
    matchmakingTitle.style.fontSize = '20px';
    matchmakingCount.style.fontSize = '14px';
    matchmakingCount.style.padding = '6px 8px';
    matchmakingCountdown.style.fontSize = '26px';
    matchmakingCountdown.style.padding = '8px 10px';
    matchmakingStatus.style.fontSize = '13px';
    matchmakingStatus.style.lineHeight = '1.15';
    matchmakingPills.style.display = 'flex';
    matchmakingSlots.style.gap = '6px';

    playerHud.style.top = 'max(254px, calc(env(safe-area-inset-top) + 254px))';
    playerHud.style.left = 'max(14px, env(safe-area-inset-left))';
    playerHud.style.right = '';
    playerHud.style.bottom = '';
    playerHud.style.width = 'min(318px, calc(100vw - 28px))';
    playerHud.style.height = '';
    playerHud.style.padding = '10px';
    playerHud.style.gap = '9px';
    playerHud.style.zIndex = '';
    nicknameLabel.style.fontSize = '11px';
    nicknameRow.style.gap = '8px';
    nicknameDisplay.style.fontSize = '20px';
    nicknameEdit.style.minWidth = '82px';
    nicknameEdit.style.padding = '0 12px';
    nicknameEditRow.style.gridTemplateColumns = 'minmax(0, 1fr) auto auto';
    nicknameInput.style.gridColumn = '';
    nicknameStatus.style.fontSize = '12px';
    nicknameStatus.style.minHeight = '14px';
    playerActionRow.style.gap = '8px';
    readyToggle.style.minWidth = '82px';
    readyToggle.style.padding = '0 12px';
    customizeButton.style.minWidth = '82px';
    customizeButton.style.padding = '0 12px';

    inventoryPanel.style.right = 'max(14px, env(safe-area-inset-right))';
    inventoryPanel.style.left = '';
    inventoryPanel.style.bottom = '86px';
    inventoryPanel.style.width = 'min(280px, calc(100vw - 28px))';
    inventoryPanel.style.padding = '10px';
    inventoryPanel.style.gap = '10px';
    inventoryPreview.style.width = '54px';
    inventoryPreview.style.height = '54px';
    inventoryCount.style.fontSize = '14px';
    inventoryHint.style.fontSize = '12px';
  }

  applyWarmupResponsiveLayout();

  function applyWarmupResponsiveState(): void {
    applyWarmupResponsiveLayout();
    applyWarmupOnboardingReveal();
    queueTutorialLayout();
  }

  function currentMatchmakingCountdownMs(): number {
    if (matchmakingCountdownMs == null) return WARMUP_MATCHMAKING_COUNTDOWN_MS;
    return Math.max(0, matchmakingCountdownMs - (now() - matchmakingReceivedAt));
  }

  function buildMatchmakingSlot(
    index: number,
    player: WarmupMatchmakingPlayer | null,
  ): HTMLElement {
    const slot = document.createElement('div');
    slot.dataset.matchmakingSlot = player?.id ?? `bot-${index}`;
    slot.setAttribute('title', player?.name ?? 'Bot fill');
    slot.setAttribute('aria-label', player?.name ?? 'Bot fill');
    slot.style.cssText = [
      'display: grid',
      'place-items: center',
      'width: 28px',
      'height: 28px',
      'box-sizing: border-box',
      'border-radius: 50%',
      'background: rgba(0,0,0,0.28)',
      player == null
        ? 'border: 1px dashed rgba(255,255,255,0.28)'
        : 'border: 1px solid rgba(255,255,255,0.18)',
      'box-shadow: 0 2px 6px rgba(0,0,0,0.24)',
    ].join('; ');

    const avatar = document.createElement('div');
    avatar.style.cssText = [
      'width: 22px',
      'height: 22px',
      'border-radius: 50%',
      'display: grid',
      'place-items: center',
      player == null
        ? 'background: rgba(255,255,255,0.10)'
        : `background: ${colorIdToCss(colorForMatchmakingPlayer(player))}`,
      'box-shadow: inset 0 0 0 2px rgba(255,255,255,0.18)',
    ].join('; ');
    slot.appendChild(avatar);

    const initial = document.createElement('span');
    initial.textContent = player == null ? 'B' : player.name.trim().charAt(0).toUpperCase() || '?';
    initial.style.cssText = [
      `font-family: ${FONT_HEADLINE}`,
      'font-size: 11px',
      'font-weight: 800',
      'line-height: 1',
      player == null ? 'color: rgba(255,255,255,0.55)' : 'color: #FFFFFF',
      'text-shadow: 0 1px 2px rgba(0,0,0,0.70)',
    ].join('; ');
    avatar.appendChild(initial);

    return slot;
  }

  function colorForMatchmakingPlayer(player: WarmupMatchmakingPlayer): ColorId {
    if (isColorId(player.color)) return player.color;
    const stateColor = latestState?.players.get(player.id)?.customization.color;
    if (isColorId(stateColor)) return stateColor;
    return 'red';
  }

  function deriveMatchmakingPlayersFromState(state: GameState | null): WarmupMatchmakingPlayer[] {
    if (state == null) return [];
    return [...state.players.values()]
      .filter((player) => player.isBot !== true && player.isReady === true)
      .map((player) => ({
        id: player.id,
        name: player.name,
        color: player.customization.color,
      }));
  }

  function ensureInitialMatchmakingFromState(): void {
    if (matchmakingPlayers.length > 0 || latestState == null) return;
    matchmakingPlayers = deriveMatchmakingPlayersFromState(latestState);
    matchmakingCapacity = latestState.settings.playerCount || WARMUP_MATCHMAKING_CAPACITY;
    matchmakingCountdownMs = matchmakingPlayers.length > 0 ? WARMUP_MATCHMAKING_COUNTDOWN_MS : null;
    matchmakingReceivedAt = now();
  }

  function snapshotMatchmaking(): WarmupMatchmakingSnapshot {
    return {
      players: [...matchmakingPlayers],
      capacity: matchmakingCapacity,
      countdownMs:
        matchmakingCountdownMs == null ? null : Math.max(0, currentMatchmakingCountdownMs()),
      receivedAt: now(),
      statusText: matchmakingStatusText,
    };
  }

  function refreshMatchmakingHud(): void {
    const capacity = Math.max(1, matchmakingCapacity);
    matchmakingCount.textContent = `${Math.min(matchmakingPlayers.length, capacity)}/${capacity}`;
    matchmakingCountdown.textContent = formatWarmupCountdown(currentMatchmakingCountdownMs());
    matchmakingStatus.textContent = matchmakingStatusText;
    matchmakingSlots.replaceChildren();
    for (let index = 0; index < capacity; index++) {
      matchmakingSlots.appendChild(buildMatchmakingSlot(index, matchmakingPlayers[index] ?? null));
    }
  }

  function updateMatchmakingCountdown(): void {
    if (disposed) return;
    matchmakingCountdown.textContent = formatWarmupCountdown(currentMatchmakingCountdownMs());
  }

  function startMatchmakingTicker(): void {
    matchmakingTicker?.dispose();
    matchmakingTicker = ctx.gsapRegistry.create('warmup-matchmaking:ticker');
    const totalTicks = Math.ceil(WARMUP_MATCHMAKING_MAX_SECONDS / WARMUP_MATCHMAKING_TICK_SECONDS);
    for (let i = 1; i <= totalTicks; i++) {
      matchmakingTicker.timeline.call(
        updateMatchmakingCountdown,
        undefined,
        i * WARMUP_MATCHMAKING_TICK_SECONDS,
      );
    }
  }

  function applyMatchmakingStatus(message: WarmupMatchmakingStatusMessage): void {
    matchmakingPlayers = message.players;
    matchmakingCapacity = message.capacity;
    matchmakingCountdownMs = message.countdownMs;
    matchmakingReceivedAt = now();
    matchmakingStatusText = message.statusText;
    refreshMatchmakingHud();
    if (message.countdownMs == null) {
      matchmakingTicker?.dispose();
      matchmakingTicker = null;
    } else {
      startMatchmakingTicker();
    }
  }

  function getLocalPlayer(): Player | null {
    if (latestState == null || localPlayerId == null) return null;
    return latestState.players.get(localPlayerId) ?? null;
  }

  function getCurrentPlayerName(): string {
    return getLocalPlayer()?.name ?? preferredPlayerName;
  }

  function getCurrentCustomization(): CharacterCustomization | null {
    return getLocalPlayer()?.customization ?? readPreferredCustomization();
  }

  function syncWarmupReadyFromState(): void {
    const ready = getLocalPlayer()?.isReady;
    if (typeof ready === 'boolean') {
      if (pendingWarmupReady !== null) {
        if (ready === pendingWarmupReady) {
          pendingWarmupReady = null;
          warmupReady = ready;
        }
        return;
      }
      warmupReady = ready;
    }
  }

  function setWarmupTutorialActive(active: boolean): void {
    void active;
    payload.warmupTutorialInput?.setActive(false);
    applyWarmupOnboardingReveal();
  }

  function currentTutorialStep(): WarmupTutorialStep | null {
    return WARMUP_TUTORIAL_STEPS[warmupTutorial.stepIndex] ?? null;
  }

  function getTutorialTarget(stepId: WarmupTutorialStepId): HTMLElement | null {
    if (stepId === 'open-items') return inventoryPanel;
    if (stepId === 'use-item') {
      return getTutorialRightAvoidance() > 0 ? null : inventoryPanel;
    }
    if (stepId === 'ready') return playerHud;
    if (stepId === 'game-modes') return topActionRow;
    if (stepId === 'menu') {
      if (isPhoneWarmupLayout()) return null;
      const globalMenu = document.querySelector<HTMLElement>('[data-global-settings="true"]');
      if (globalMenu == null) return menuTutorialTarget;
      const rect = globalMenu.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 ? globalMenu : menuTutorialTarget;
    }
    if (stepId === 'customize') return customizeButton;
    return null;
  }

  function tutorialStepsWithTargets(): readonly LiveTutorialStep[] {
    return WARMUP_TUTORIAL_STEPS.map((step) => ({
      ...step,
      target: () => getTutorialTarget(step.id),
    }));
  }

  function getTutorialRightAvoidance(): number {
    const avoidTargets = [
      document.querySelector<HTMLElement>('[data-screen-id="inventory"], .screen-inventory-view'),
      customizationOverlayRoot,
    ];
    return avoidTargets.reduce((maxWidth, target) => {
      if (target == null) return maxWidth;
      const rect = target.getBoundingClientRect();
      const inlineWidth = Number.parseFloat(target.style.width);
      const fallbackWidth = target === customizationOverlayRoot ? 360 : 0;
      const width =
        rect.width > 0
          ? Math.ceil(rect.width)
          : Number.isFinite(inlineWidth)
            ? Math.ceil(inlineWidth)
            : fallbackWidth;
      return Math.max(maxWidth, width);
    }, 0);
  }

  function getTutorialProgressRightAvoidance(): number {
    if (!isPhoneWarmupLayout()) return 0;
    const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
    const controls = Array.from(
      document.querySelectorAll<HTMLElement>(
        '[data-global-settings="true"], [data-global-discord="true"], [data-global-pause="true"]',
      ),
    );
    const leftmostVisible = controls.reduce((leftmost, control) => {
      const rect = control.getBoundingClientRect();
      const style = getComputedStyle(control);
      const visible =
        rect.width > 0 &&
        rect.height > 0 &&
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        Number(style.opacity || '1') > 0.01;
      return visible ? Math.min(leftmost, rect.left) : leftmost;
    }, Number.POSITIVE_INFINITY);
    if (!Number.isFinite(leftmostVisible)) return 0;
    return Math.max(0, Math.ceil(viewportWidth - leftmostVisible + 8));
  }

  function queueTutorialLayout(): void {
    requestAnimationFrame(() => {
      tutorialHandle?.layout();
    });
  }

  function isMobileTutorialFocusActive(): boolean {
    return tutorialHandle != null && !hasSeenWarmupTutorial() && isPhoneWarmupLayout();
  }

  function setVisible(element: HTMLElement, visible: boolean, display: string): void {
    element.style.display = visible ? display : 'none';
  }

  function disposeWarmupTutorialWidget(): void {
    tutorialHandle?.dispose();
    tutorialHandle = null;
    payload.warmupTutorialInput?.setAdvanceHandler(null);
    payload.warmupTutorialInput?.setActive(false);
  }

  function setWarmupHudSuppressed(suppressed: boolean): void {
    warmupHudSuppressed = suppressed;
    if (suppressed) {
      hideJoinPanel();
      closeCustomizationOverlay();
      disposeWarmupTutorialWidget();
    }
    applyWarmupResponsiveState();
  }

  function applyWarmupOnboardingReveal(): void {
    if (warmupHudSuppressed) {
      setVisible(tray, false, 'none');
      setVisible(topActionRow, false, 'none');
      setVisible(bottomActionRow, false, 'none');
      setVisible(watchBackRow, false, 'none');
      setVisible(matchmakingHud, false, 'none');
      setVisible(playerHud, false, 'none');
      setVisible(inventoryPanel, false, 'none');
      joinPanel.style.display = 'none';
      return;
    }

    const onboardingActive = tutorialHandle != null && !hasSeenWarmupTutorial();
    const stepId = currentTutorialStep()?.id ?? null;
    if (isMobileTutorialFocusActive()) {
      const showTray = stepId === 'game-modes';
      const showPlayer = stepId === 'ready' || stepId === 'customize';
      const showInventory = stepId === 'open-items' || stepId === 'use-item';
      setVisible(tray, showTray, 'flex');
      setVisible(topActionRow, showTray, isPhoneWarmupLayout() ? 'grid' : 'flex');
      setVisible(bottomActionRow, false, 'none');
      setVisible(watchBackRow, false, 'none');
      setVisible(matchmakingHud, false, 'none');
      setVisible(playerHud, showPlayer, 'flex');
      setVisible(inventoryPanel, showInventory, 'flex');
      if (!showTray) hideJoinPanel();
      if (stepId !== 'customize') closeCustomizationOverlay();
      return;
    }

    const fullUiVisible =
      !onboardingActive ||
      stepId === 'ready' ||
      stepId === 'game-modes' ||
      stepId === 'menu' ||
      stepId === 'customize' ||
      stepId === 'complete';
    const inventoryVisible =
      !onboardingActive ||
      stepId === 'open-items' ||
      stepId === 'use-item' ||
      stepId === 'ready' ||
      stepId === 'game-modes' ||
      stepId === 'menu' ||
      stepId === 'customize' ||
      stepId === 'complete';

    tray.style.display = fullUiVisible ? 'flex' : 'none';
    matchmakingHud.style.display = fullUiVisible ? 'flex' : 'none';
    playerHud.style.display = fullUiVisible ? 'flex' : 'none';
    inventoryPanel.style.display = inventoryVisible ? 'flex' : 'none';

    if (!fullUiVisible) {
      joinPanel.style.display = 'none';
      hideJoinPanel();
      closeCustomizationOverlay();
    }
  }

  function captureTutorialBaseline(): void {
    const player = getLocalPlayer();
    warmupTutorial.baseline = {
      nodeId: player?.nodeId ?? null,
      hp: player?.hp ?? null,
      keys: player?.keys ?? null,
      itemCount: player?.items.length ?? 0,
      eventCount: latestState?.eventLog.length ?? 0,
      customization: cloneCustomization(player?.customization ?? null),
    };
  }

  function advanceWarmupTutorial(reason: string): void {
    void reason;
    if (tutorialHandle == null) return;
    tutorialHandle.advance();
    if (tutorialHandle != null) {
      captureTutorialBaseline();
      tutorialHandle.layout();
    }
  }

  function maybeAdvanceWarmupTutorialFromState(): void {
    const tutorialBaseline = warmupTutorial.baseline;
    if (tutorialHandle == null || tutorialBaseline == null) return;
    const step = currentTutorialStep();
    const player = getLocalPlayer();
    if (step == null || player == null) return;

    if (step.id === 'arena-effect') {
      const itemCountChanged = player.items.length !== tutorialBaseline.itemCount;
      const itemEvent = (latestState?.eventLog ?? [])
        .slice(tutorialBaseline.eventCount)
        .some((event) => event.type === 'player_received_item' && event.playerId === localPlayerId);
      if (itemCountChanged || itemEvent) {
        advanceWarmupTutorial('arena-effect');
        return;
      }
    }

    if (step.id === 'use-item') {
      const events = latestState?.eventLog ?? [];
      const itemWasCast = events
        .slice(tutorialBaseline.eventCount)
        .some(
          (event) =>
            event.type === 'item_cast' &&
            (event.playerId === localPlayerId || event.casterId === localPlayerId),
        );
      const itemCountDropped = player.items.length < tutorialBaseline.itemCount;
      if (itemWasCast || itemCountDropped) {
        advanceWarmupTutorial('item-used');
      }
    }

    if (
      step.id === 'customize' &&
      warmupTutorial.customizeOpened &&
      hasCustomizationChanged(tutorialBaseline.customization, player.customization)
    ) {
      advanceWarmupTutorial('customized');
    }
  }

  function maybeAdvanceWarmupTutorialFromCustomization(
    customization: CharacterCustomization,
  ): void {
    const tutorialBaseline = warmupTutorial.baseline;
    if (tutorialHandle == null || tutorialBaseline == null) return;
    if (currentTutorialStep()?.id !== 'customize') return;
    if (!warmupTutorial.customizeOpened) return;
    if (hasCustomizationChanged(tutorialBaseline.customization, customization)) {
      advanceWarmupTutorial('customized');
    }
  }

  function refreshPlayerPanel(): void {
    const customizationUnavailable =
      payload.characterAdapter == null || customizationCosmeticsLoading;
    if (nicknameEditRow.style.display !== 'grid') {
      nicknameDisplay.textContent = getCurrentPlayerName();
      nicknameInput.value = getCurrentPlayerName();
    }
    readyToggle.textContent = warmupReady ? 'Unready' : 'Ready';
    readyToggle.style.background = warmupReady
      ? 'rgba(255,255,255,0.08)'
      : 'linear-gradient(to bottom, #F0B838, #C4841C)';
    readyToggle.style.borderColor = warmupReady ? 'rgba(255,255,255,0.16)' : '#8A5A10';
    readyToggle.style.color = '#FFFFFF';
    readyToggle.style.textShadow = warmupReady ? '' : '0 1px 0 #000000, 0 2px 6px rgba(0,0,0,0.45)';
    customizeButton.textContent = customizationCosmeticsLoading ? 'Loading...' : 'Customize';
    customizeButton.disabled = customizationUnavailable;
    customizeButton.style.opacity = customizationUnavailable ? '0.55' : '1';
    customizeButton.style.cursor = customizationUnavailable ? 'not-allowed' : 'pointer';
  }

  function showNicknameEditor(): void {
    nicknameStatus.textContent = '';
    nicknameInput.value = getCurrentPlayerName();
    nicknameRow.style.display = 'none';
    nicknameEditRow.style.display = 'grid';
    nicknameInput.focus();
    nicknameInput.select();
  }

  function hideNicknameEditor(): void {
    nicknameEditRow.style.display = 'none';
    nicknameRow.style.display = 'grid';
    nicknameStatus.textContent = '';
    refreshPlayerPanel();
  }

  function saveNicknameEdit(): void {
    const nextName = savePreferredPlayerName(nicknameInput.value);
    if (nextName == null) {
      nicknameStatus.textContent = 'Enter 1-16 characters.';
      nicknameStatus.style.color = '#FFB0A0';
      nicknameInput.focus();
      return;
    }
    preferredPlayerName = nextName;
    nicknameStatus.textContent = 'Name saved.';
    nicknameStatus.style.color = '#B0B8C8';
    payload.send({
      type: 'set_player_name',
      playerName: nextName,
      ...(localPlayerId == null ? {} : { playerId: localPlayerId }),
    });
    hideNicknameEditor();
  }

  function setWarmupReady(nextReady: boolean): void {
    pendingWarmupReady = nextReady;
    warmupReady = nextReady;
    refreshPlayerPanel();
    payload.send({
      type: 'set_ready',
      isReady: nextReady,
      ...(localPlayerId == null ? {} : { playerId: localPlayerId }),
    });
  }

  function closeCustomizationOverlay(): void {
    const wasOpen = isCustomizationOverlayOpen();
    customizationOverlayHandle?.dispose();
    customizationOverlayHandle = null;
    customizationOverlayRoot?.remove();
    customizationOverlayRoot = null;
    if (wasOpen) payload.onCustomizeCamera?.(null);
  }

  function areCustomizationCosmeticsReady(): boolean {
    return payload.areCharacterCosmeticsLoaded?.() ?? true;
  }

  async function ensureCustomizationCosmeticsReady(): Promise<boolean> {
    if (areCustomizationCosmeticsReady()) return true;
    const ensure = payload.ensureCharacterCosmeticsLoaded;
    if (ensure == null) return false;
    customizationCosmeticsLoading = true;
    refreshPlayerPanel();
    try {
      await ensure();
    } catch (error: unknown) {
      console.warn('[smashparty] character cosmetics preload failed:', error);
      return false;
    } finally {
      customizationCosmeticsLoading = false;
      refreshPlayerPanel();
    }
    return areCustomizationCosmeticsReady();
  }

  async function openCustomizationOverlayAfterPreload(): Promise<void> {
    if (payload.characterAdapter == null) return;
    if (await ensureCustomizationCosmeticsReady()) {
      mountCustomizationOverlay();
    }
  }

  function openCustomizationOverlay(): void {
    if (payload.characterAdapter == null) return;
    if (currentTutorialStep()?.id === 'customize') {
      warmupTutorial.customizeOpened = true;
    }
    if (!areCustomizationCosmeticsReady()) {
      void openCustomizationOverlayAfterPreload();
      return;
    }
    mountCustomizationOverlay();
  }

  function mountCustomizationOverlay(): void {
    if (payload.characterAdapter == null || disposed) return;
    closeCustomizationOverlay();

    const selectHost = document.createElement('div');
    selectHost.dataset.testid = 'arena-customize-overlay';
    selectHost.className = 'lobby-responsive__inline-select';
    selectHost.style.cssText = [
      'position: absolute',
      'top: max(14px, env(safe-area-inset-top))',
      'right: max(14px, env(safe-area-inset-right))',
      'bottom: max(14px, env(safe-area-inset-bottom))',
      'width: min(340px, calc(100vw - 28px))',
      'display: flex',
      'min-height: 0',
      'z-index: 540',
      'pointer-events: auto',
    ].join('; ');

    const localPlayer = getLocalPlayer();
    const takenColors: ColorId[] =
      latestState == null
        ? []
        : [...latestState.players.values()]
            .filter((player) => player.id !== localPlayerId)
            .map((player) => player.customization.color);
    const initial: CharacterCustomization = getCurrentCustomization() ?? {
      color: 'red',
      ...DEFAULT_CHARACTER_CUSTOMIZATION,
    };

    customizationOverlayRoot = selectHost;
    root.appendChild(selectHost);

    customizationOverlayHandle = createCharacterSelectScreen({
      gsapRegistry: ctx.gsapRegistry,
      host: selectHost,
      router,
      payload: {
        adapter: payload.characterAdapter,
        takenColors,
        initial,
        ready: warmupReady,
        playerName: localPlayer?.name ?? preferredPlayerName,
        mode: 'inline',
        onCustomizeCamera: (view) => {
          if (localPlayerId != null) {
            payload.onCustomizeCamera?.({ playerId: localPlayerId, view });
          }
        },
        onSelect: (customization) => {
          payload.send({
            type: 'set_character_customization',
            customization,
            ...(localPlayerId == null ? {} : { playerId: localPlayerId }),
          });
          maybeAdvanceWarmupTutorialFromCustomization(customization);
        },
        onReady: (customization, isReady) => {
          payload.send({
            type: 'set_character_customization',
            customization,
            ...(localPlayerId == null ? {} : { playerId: localPlayerId }),
          });
          maybeAdvanceWarmupTutorialFromCustomization(customization);
          setWarmupReady(isReady);
        },
      },
    });
    customizationOverlayHandle.element.style.pointerEvents = 'auto';

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.dataset.testid = 'arena-customize-close';
    closeButton.setAttribute('aria-label', 'Close character customization');
    closeButton.textContent = 'X';
    closeButton.style.cssText = [
      'position: absolute',
      isPhoneWarmupLayout() && tutorialHandle != null ? 'top: 82px' : 'top: 8px',
      'left: 8px',
      'z-index: 5',
      'width: 48px',
      'height: 48px',
      'display: grid',
      'place-items: center',
      'border-radius: 999px',
      'border: 1px solid rgba(255,255,255,0.24)',
      'background: rgba(12,16,24,0.84)',
      'color: #FFFFFF',
      `font-family: ${FONT_HEADLINE}`,
      'font-size: 18px',
      'font-weight: 800',
      'line-height: 1',
      'cursor: pointer',
      'touch-action: manipulation',
      'box-shadow: 0 4px 12px rgba(0,0,0,0.35)',
    ].join('; ');
    closeButton.addEventListener('click', closeCustomizationOverlay);
    selectHost.appendChild(closeButton);
  }

  function isCustomizationOverlayOpen(): boolean {
    return customizationOverlayHandle != null || customizationOverlayRoot != null;
  }

  function toggleCustomizationOverlay(): void {
    if (isCustomizationOverlayOpen()) {
      closeCustomizationOverlay();
      return;
    }
    openCustomizationOverlay();
  }

  function buildWarmupOverlayPayload(): WarmupArenaOverlayPayload {
    return {
      ...payload,
      initialPlayerId: localPlayerId,
      initialState: latestState,
      initialMatchmaking: snapshotMatchmaking(),
      warmupTutorial,
    };
  }

  function buildWarmupPausePayload(): PauseMenuPayload {
    return {
      isHost: true,
      roomType: 'create-game',
      title: 'MENU',
      subtitle: 'Warmup arena',
      resumeLabel: 'RESUME',
      leaveLabel: 'LEAVE',
      readyToggleLabel: warmupReady ? 'UNREADY' : 'READY',
      onResume: () => router.navigate('warmup-arena-overlay', buildWarmupOverlayPayload()),
      onSettings: () => {
        router.navigate('settings', {
          backTarget: {
            screenId: 'pause-menu',
            payload: buildWarmupPausePayload(),
          },
        });
      },
      onHowToPlay: () => {
        router.navigate('how-to-play', {
          onBack: () => router.navigate('pause-menu', buildWarmupPausePayload()),
        });
      },
      onReadyToggle: () => {
        setWarmupReady(!warmupReady);
        router.navigate('pause-menu', buildWarmupPausePayload());
      },
      onLeaveGame: () => {
        payload.send({ type: 'leave' });
        payload.send(
          buildWarmupSubscribeMessage({
            preferredColor: payload.portal?.colorHex,
            isReady: hasSeenWarmupTutorial(),
          }),
        );
        router.navigate('warmup-arena-overlay', buildWarmupOverlayPayload());
      },
    };
  }

  function openWarmupMenu(): void {
    closeCustomizationOverlay();
    router.navigate('pause-menu', buildWarmupPausePayload());
  }

  function makeWarmupInventoryPayload(state: GameState): InventoryViewPayload | null {
    const playerId = localPlayerId;
    if (playerId == null || !state.players.has(playerId)) return null;
    return {
      state,
      localPlayerId: playerId,
      send: (msg) => payload.send(msg),
      subscribe: (handler) => payload.subscribe((raw) => handler(raw as ServerMessage)),
      playSfx: payload.playSfx,
      previewItemInHand: payload.previewItemInHand,
      clearItemInHandPreview: payload.clearItemInHandPreview,
      isActionControlLocked: payload.isActionControlLocked,
      subscribeActionControlLock: payload.subscribeActionControlLock,
      startItemPlayerTargeting: payload.startItemPlayerTargeting,
      startItemDirectionalTargeting: payload.startItemDirectionalTargeting,
      startItemRangePreview: payload.startItemRangePreview,
      getPlayerNotificationAnchor: payload.getPlayerNotificationAnchor,
      itemMeta: buildInventoryItemMeta('warmup-arena'),
      inventoryMode: 'use',
      itemUseContext: 'warmup-arena',
      itemCancelMode: 'return',
      itemReturnTarget: (nextState) => ({
        screenId: 'warmup-arena-overlay',
        payload: {
          ...payload,
          initialPlayerId: playerId,
          initialState: nextState,
          initialMatchmaking: snapshotMatchmaking(),
          warmupTutorial,
        },
      }),
    };
  }

  function openInventory(): void {
    if (latestState == null) return;
    const inventoryPayload = makeWarmupInventoryPayload(latestState);
    if (inventoryPayload == null) return;
    if (currentTutorialStep()?.id === 'open-items') {
      advanceWarmupTutorial('inventory-opened');
    }
    if ((tutorialHandle != null || isCustomizationOverlayOpen()) && router.showOverlay != null) {
      router.showOverlay('inventory', {
        ...inventoryPayload,
        onClose: () => {
          router.closeOverlay?.('inventory');
          queueTutorialLayout();
        },
      });
      queueTutorialLayout();
      return;
    }
    router.navigate('inventory', inventoryPayload);
  }

  function isBlockingWarmupModalOpen(): boolean {
    return joinModalHandle != null;
  }

  function isPointInsideInventoryPanel(clientX: number, clientY: number): boolean {
    const rect = inventoryPanel.getBoundingClientRect();
    return (
      rect.width > 0 &&
      rect.height > 0 &&
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    );
  }

  function handleInventoryPointerUp(event: PointerEvent): void {
    if (disposed || isBlockingWarmupModalOpen()) return;
    if (event.button !== 0) return;
    if (!isPointInsideInventoryPanel(event.clientX, event.clientY)) return;
    event.preventDefault();
    event.stopPropagation();
    openInventory();
  }

  function refreshInventoryPanel(): void {
    const items = getLocalPlayer()?.items ?? [];
    inventoryCount.textContent = `Items (${items.length})`;
    inventoryHint.textContent = items.length > 0 ? 'Open shared item UI' : 'Grab item tiles';
    const firstItem = items[0] ?? null;
    inventoryPreview.replaceChildren();
    if (firstItem == null) {
      inventoryPreview.textContent = 'I';
      return;
    }
    const img = document.createElement('img');
    img.src = itemIconUrl(firstItem);
    img.alt = '';
    img.draggable = false;
    img.style.cssText = ['width: 100%', 'height: 100%', 'display: block', 'object-fit: cover'].join(
      '; ',
    );
    inventoryPreview.appendChild(img);
  }

  function updateFromServerMessage(message: ServerMessage): void {
    if (message.type === 'notification' && message.text === 'color_taken') {
      closeCustomizationOverlay();
      return;
    }
    if (message.type === 'warmup_arena_joined') {
      localPlayerId = message.playerId;
      if (typeof message.playerName === 'string') {
        preferredPlayerName = message.playerName;
      }
      if (typeof message.isReady === 'boolean') {
        warmupReady = message.isReady;
      }
      syncWarmupReadyFromState();
      refreshPlayerPanel();
      refreshInventoryPanel();
      refreshMatchmakingHud();
      maybeShowWarmupTutorial();
      maybeAdvanceWarmupTutorialFromState();
      return;
    }
    if (message.type === 'warmup_matchmaking_status') {
      applyMatchmakingStatus(message);
      return;
    }
    if (message.type !== 'state_sync') return;
    const shouldRemountCustomization =
      customizationOverlayHandle != null || customizationOverlayRoot != null;
    const state = deserializeGameState(message.state);
    latestState = state;
    if (localPlayerId == null && state.players.size === 1) {
      localPlayerId = state.turnOrder[0] ?? null;
    }
    const authoritativeCustomization = getLocalPlayer()?.customization;
    if (authoritativeCustomization != null) {
      savePreferredCustomization(authoritativeCustomization);
    }
    syncWarmupReadyFromState();
    matchmakingCapacity = state.settings.playerCount;
    refreshPlayerPanel();
    refreshInventoryPanel();
    refreshMatchmakingHud();
    maybeShowWarmupTutorial();
    maybeAdvanceWarmupTutorialFromState();
    if (shouldRemountCustomization) {
      openCustomizationOverlay();
    }
  }

  const unsubscribe = payload.subscribe((raw) => updateFromServerMessage(raw as ServerMessage));
  syncWarmupReadyFromState();
  ensureInitialMatchmakingFromState();
  refreshMatchmakingHud();
  refreshPlayerPanel();
  refreshInventoryPanel();
  applyWarmupOnboardingReveal();

  function handleSoloMinigame(): void {
    const preferredCustomization = readPreferredCustomization();
    payload.send({
      type: 'start_solo_minigame',
      playerName: readPreferredPlayerName(),
      ...(preferredCustomization == null ? {} : { customization: preferredCustomization }),
    });
  }

  function handleMinigamesOnly(): void {
    router.navigate('lobby-host', {
      ...getLobbyPayloadBase(payload),
      mode: 'minigames_only',
      roomType: 'create_game',
      escBackTarget: warmupBackTarget,
    });
  }

  function handleHostGame(): void {
    router.navigate('lobby-host', {
      ...getLobbyPayloadBase(payload),
      mode: 'board_game',
      roomType: 'create_game',
      escBackTarget: warmupBackTarget,
    });
  }

  function showJoinPanel(): void {
    joinModalHandle?.dispose();
    const preferredCustomization = readPreferredCustomization();
    joinModalHandle = openJoinRoomModal({
      mount: root,
      send: (msg) => payload.send(msg),
      subscribe: (handler) => payload.subscribe((msg) => handler(msg as ServerMessage)),
      playerName: getCurrentPlayerName(),
      ...(preferredCustomization == null ? {} : { preferredCustomization }),
      onAccepted: (joined) => {
        joinModalHandle = null;
        router.navigate('lobby-joiner', {
          ...getLobbyPayloadBase(payload),
          mode: joined.mode === 'minigames_only' ? 'minigames_only' : 'board_game',
          roomType: joined.roomType,
          code: joined.roomCode,
          skipInitialRequest: true,
          restoredSession: {
            playerId: joined.playerId,
            hostId: joined.hostId,
            roomCode: joined.roomCode,
          },
          escBackTarget: warmupBackTarget,
        });
      },
      onCancel: () => {
        joinModalHandle = null;
      },
    });
    applyWarmupOnboardingReveal();
  }

  function hideJoinPanel(): void {
    joinModalHandle?.dispose();
    joinModalHandle = null;
  }

  function handleJoinGame(): void {
    showJoinPanel();
  }

  function handleJoinSubmit(event: SubmitEvent): void {
    event.preventDefault();
    const code = joinInput.value.trim().toUpperCase();
    if (code.length === 0) {
      joinInput.style.borderColor = '#D94040';
      joinInput.focus();
      return;
    }
    router.navigate('lobby-joiner', {
      ...getLobbyPayloadBase(payload),
      mode: 'board_game',
      roomType: 'create_game',
      code,
      escBackTarget: warmupBackTarget,
    });
  }

  function handleJoinInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      hideJoinPanel();
    }
  }

  function handleHowToPlay(): void {
    router.navigate('how-to-play', {
      onBack: () => router.navigate('warmup-arena-overlay', buildWarmupOverlayPayload()),
    });
  }

  function handleWatchActiveGame(): void {
    window.dispatchEvent(new CustomEvent('smashparty:warmup-watch-active-game'));
    showWatchBackButton();
  }

  function handleInventoryKeydown(event: KeyboardEvent): void {
    if (event.key !== 'i' && event.key !== 'I' && event.code !== 'KeyI') return;
    if (isTypingTarget(event.target)) return;
    event.preventDefault();
    openInventory();
  }

  function handleWarmupEscape(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    if (router.overlayCurrent === 'inventory') return;
    if (isCustomizationOverlayOpen()) {
      event.preventDefault();
      closeCustomizationOverlay();
      return;
    }
    if (isTypingTarget(event.target)) return;
    event.preventDefault();
    openWarmupMenu();
  }

  function handleNicknameInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      saveNicknameEdit();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      hideNicknameEditor();
    }
  }

  function handleReadyToggleClick(): void {
    setWarmupReady(!warmupReady);
  }

  function handleReturnPortal(): void {
    if (payload.portal == null) return;
    window.location.assign(buildPortalReturnUrl(payload.portal));
  }

  function showWatchBackButton(): void {
    if (watchBackButton == null) {
      watchBackButton = makePanelButton('Back', 'primary');
      watchBackButton.dataset.testid = 'arena-watch-back';
      watchBackButton.addEventListener('click', handleWatchBackClick);
      watchBackRow.appendChild(watchBackButton);
    }
    watchBackRow.style.display = 'flex';
    applyWarmupResponsiveLayout();
  }

  function hideWatchBackButton(): void {
    watchBackButton?.removeEventListener('click', handleWatchBackClick);
    watchBackButton?.remove();
    watchBackButton = null;
    watchBackRow.style.display = 'none';
    applyWarmupResponsiveLayout();
  }

  function handleWatchBackClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    hideWatchBackButton();
    window.dispatchEvent(new CustomEvent('smashparty:warmup-watch-arena'));
  }

  function maybeShowWarmupTutorial(): void {
    if (disposed || warmupHudSuppressed || localPlayerId == null || hasSeenWarmupTutorial()) return;
    if (tutorialHandle != null) return;
    if (warmupTutorial.stepIndex >= WARMUP_TUTORIAL_STEPS.length) return;

    if (warmupTutorial.baseline == null) {
      captureTutorialBaseline();
    }
    setWarmupTutorialActive(true);
    payload.warmupTutorialInput?.setAdvanceHandler(null);
    tutorialHandle = createLiveTutorialWidget({
      mount: host,
      steps: tutorialStepsWithTargets(),
      testId: 'arena-first-tutorial',
      zIndex: TUTORIAL_Z_INDEX,
      initialStepIndex: warmupTutorial.stepIndex,
      showHideButton: false,
      mobileLayout: 'center',
      getRightAvoidance: getTutorialRightAvoidance,
      getProgressRightAvoidance: getTutorialProgressRightAvoidance,
      onHideTutorials: () => setTutorialsHidden(true),
      canAdvance: (_index, step) => {
        const warmupStep = step as WarmupTutorialStep;
        if (warmupStep.id !== 'menu' || areCustomizationCosmeticsReady()) return true;
        void ensureCustomizationCosmeticsReady();
        return false;
      },
      onStepChange: (index, step) => {
        const stepChanged = index !== warmupTutorial.stepIndex;
        const previousStepId = currentTutorialStep()?.id ?? null;
        const warmupStep = step as WarmupTutorialStep;
        warmupTutorial.stepIndex = index;
        if (stepChanged) {
          warmupTutorial.customizeOpened =
            warmupStep.id === 'customize' && isCustomizationOverlayOpen();
        }
        if (stepChanged && previousStepId === 'menu' && warmupStep.id === 'customize') {
          router.closeOverlay?.('pause-menu');
        }
        if (stepChanged || warmupTutorial.baseline == null) {
          captureTutorialBaseline();
        }
        if (warmupStep.id === 'menu' || warmupStep.id === 'customize') {
          void ensureCustomizationCosmeticsReady();
        }
        applyWarmupResponsiveState();
      },
      onComplete: () => {
        tutorialHandle = null;
        payload.warmupTutorialInput?.setAdvanceHandler(null);
        warmupTutorial.stepIndex = WARMUP_TUTORIAL_STEPS.length;
        warmupTutorial.baseline = null;
        setWarmupTutorialActive(false);
        markWarmupTutorialSeen();
        setWarmupReady(true);
        applyWarmupResponsiveState();
      },
    });
    applyWarmupResponsiveState();
  }

  function handleWarmupHudSuppress(): void {
    setWarmupHudSuppressed(true);
  }

  function handleWarmupHudRestore(): void {
    setWarmupHudSuppressed(false);
  }

  const handlers: Record<string, () => void> = {
    'solo-minigame': handleSoloMinigame,
    'minigames-only': handleMinigamesOnly,
    'host-game': handleHostGame,
    'join-game': handleJoinGame,
    'how-to-play': handleHowToPlay,
    'watch-active-game': handleWatchActiveGame,
    'return-portal': handleReturnPortal,
  };

  for (const [key, el] of cardEls) {
    const handler = handlers[key];
    if (handler != null) el.addEventListener('click', handler);
  }
  nicknameEdit.addEventListener('click', showNicknameEditor);
  nicknameSave.addEventListener('click', saveNicknameEdit);
  nicknameCancel.addEventListener('click', hideNicknameEditor);
  nicknameInput.addEventListener('keydown', handleNicknameInputKeydown);
  readyToggle.addEventListener('click', handleReadyToggleClick);
  customizeButton.addEventListener('click', toggleCustomizationOverlay);
  inventoryPanel.addEventListener('click', openInventory);
  document.addEventListener('pointerup', handleInventoryPointerUp, true);
  joinPanel.addEventListener('submit', handleJoinSubmit);
  joinCancel.addEventListener('click', hideJoinPanel);
  joinInput.addEventListener('keydown', handleJoinInputKeydown);
  document.addEventListener('keydown', handleInventoryKeydown);
  document.addEventListener('keydown', handleWarmupEscape);
  window.addEventListener('smashparty:warmup-suppress-hud', handleWarmupHudSuppress);
  window.addEventListener('smashparty:warmup-restore-hud', handleWarmupHudRestore);
  window.addEventListener('resize', applyWarmupResponsiveState);
  window.visualViewport?.addEventListener('resize', applyWarmupResponsiveState);

  host.appendChild(root);
  maybeShowWarmupTutorial();
  maybeAdvanceWarmupTutorialFromState();

  return {
    element: root,
    dispose(): void {
      if (disposed) return;
      disposed = true;
      unsubscribe();
      matchmakingTicker?.dispose();
      matchmakingTicker = null;
      for (const [key, el] of cardEls) {
        const handler = handlers[key];
        if (handler != null) el.removeEventListener('click', handler);
      }
      closeCustomizationOverlay();
      disposeWarmupTutorialWidget();
      setWarmupTutorialActive(false);
      joinModalHandle?.dispose();
      hideWatchBackButton();
      nicknameEdit.removeEventListener('click', showNicknameEditor);
      nicknameSave.removeEventListener('click', saveNicknameEdit);
      nicknameCancel.removeEventListener('click', hideNicknameEditor);
      nicknameInput.removeEventListener('keydown', handleNicknameInputKeydown);
      readyToggle.removeEventListener('click', handleReadyToggleClick);
      customizeButton.removeEventListener('click', toggleCustomizationOverlay);
      inventoryPanel.removeEventListener('click', openInventory);
      document.removeEventListener('pointerup', handleInventoryPointerUp, true);
      joinModalHandle?.dispose();
      joinModalHandle = null;
      joinPanel.removeEventListener('submit', handleJoinSubmit);
      joinCancel.removeEventListener('click', hideJoinPanel);
      joinInput.removeEventListener('keydown', handleJoinInputKeydown);
      document.removeEventListener('keydown', handleInventoryKeydown);
      document.removeEventListener('keydown', handleWarmupEscape);
      window.removeEventListener('smashparty:warmup-suppress-hud', handleWarmupHudSuppress);
      window.removeEventListener('smashparty:warmup-restore-hud', handleWarmupHudRestore);
      window.removeEventListener('resize', applyWarmupResponsiveState);
      window.visualViewport?.removeEventListener('resize', applyWarmupResponsiveState);
      root.remove();
    },
  };
}
